import React, { ChangeEvent, useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import {
  setFetchedTransportation,
  setTransportationForm
} from '../../../store/slices/transportation/transportationSlice';
import { findTransportation } from '../../../apollo/queries/findTransportation';
import { RootState } from '../../../store/store';
import { getTranslationByPath } from '../../../utils/translations';
import { addNotification } from '../../../store/slices/notification/notificationSlice';
import { MainContent } from '../../../layout/content/MainContent';
import { KeyboardWrapper } from '../../../components/keyboard/KeyboardWrapper';


export type NoTemplateType = {
  transitionUrl: TransitionUrl
  noInputLabel: TranslationPath
  noInputPlaceholder: TranslationPath
  notFoundText: TranslationPath
}

type Props = {
  page: Page<NoTemplateType>
}


export const NoTemplate = (props: Props): React.ReactElement => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const language = useLanguage();
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)
  const transportationTypeId = useSelector((state: RootState) => state.transportation.transportationTypeId)

  const [value, setValue] = useState<string>('');

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }

  const onContinue = async () => {
    if (value.length === 0) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
      ))
      return
    }

    setIsProcessing(true);

    const findTransportationResult = await findTransportation({
      value,
      findBy: 'no',
      qtirLanguageId: language.qtirLanguageId,
      transportationTypeId: transportationTypeId ? transportationTypeId : undefined,
    })

    const foundTransportation = findTransportationResult.data.findTransportation.transportation
    if (!foundTransportation) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.notFoundText) },
      ))
      setIsProcessing(false);
      return
    }

    dispatch(setFetchedTransportation(foundTransportation))
    dispatch(setTransportationForm({
      ...transportationForm,
      transportationTypeId: foundTransportation.transportationTypeId,
      registrationNumber: foundTransportation.registrationNumber,
      trailerRegistrationNumber: foundTransportation.trailerRegistrationNumber,
      trailerType: foundTransportation.trailerRegistrationNumber,
      driverName: foundTransportation.driverName,
      phoneNumber: foundTransportation.phoneNumber,
    }))

    setIsProcessing(false);
    navigate(props.page.data.transitionUrl)
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full">
              <Label.Root className="LabelRoot text-2xl mb-10" htmlFor="identification">
                {getTranslationByPath(language.translations, props.page.data.noInputLabel)}
              </Label.Root>
              <KeyboardWrapper title={getTranslationByPath(language.translations, props.page.data.noInputLabel)}>
                <TextField.Root
                  style={{ height: '50px' }}
                  className="mt-3"
                  placeholder={getTranslationByPath(language.translations, props.page.data.noInputPlaceholder)}
                  onChange={onChange}
                />
              </KeyboardWrapper>
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer backUrl={props.page.footer?.back} onContinue={onContinue} disabledBackBtn={isProcessing} disabledContinueBtn={isProcessing} />
    </>
  )
}
